import React, { useState, useEffect, useCallback } from 'react';
import { Settings, RefreshCw, CheckCircle, XCircle, AlertTriangle, Clock, Loader } from 'lucide-react';
import { neonQuery } from '../services/api';
export default function ConfigureSite() {
  const [days, setDays] = useState(3);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);

  const loadLogs = useCallback(async () => {
    try {
      const rows = await neonQuery('sync_log');
      setLogs(rows);
    } catch (e) {
      console.error('Failed to load sync logs:', e);
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const runSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      // ALWAYS the background twin, at every lookback. The synchronous /api/sync is
      // capped at ~26s by the Netlify gateway (the `timeout = 300` in netlify.toml
      // applies to background/scheduled fns only), and a ONE-day sync measures 97-130s
      // in sync_log today - so the sync endpoint returned the gateway's raw
      // "Inactivity Timeout" HTML page for every lookback the old `days > 7` test sent
      // to it, and a 3-day run also blew the 300s Lambda ceiling and died unlogged.
      // App.js's automatic 15-minute sync has always used this endpoint.
      await fetch('/api/sync-background', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ days, force: true }),
      });
      // Background functions ack with 202 BEFORE running anything, so the response
      // proves only that it was accepted - the outcome is read from sync_log below.
      setSyncResult({
        ok: true,
        message: `${days}-day sync started in the background. It takes about ${days * 2} minutes; the run appears in Sync History below when it finishes.`,
      });
      setSyncing(false);
    } catch (e) {
      setSyncResult({ ok: false, message: e.message });
      setSyncing(false);
    }
  };

  const statusIcon = (status) => {
    if (status === 'success') return <CheckCircle size={14} color="var(--green)" />;
    if (status === 'warning') return <AlertTriangle size={14} color="var(--orange)" />;
    return <XCircle size={14} color="var(--red)" />;
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <Settings size={24} color="var(--navy)" />
        <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Configure Site</h1>
      </div>

      {/* Sync Controls */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <span>Neon Database Sync</span>
        </div>
        <div style={{ padding: 20 }}>
          <div style={{
            background: '#e8f0fe', border: '1px solid #c5d7f2', borderRadius: 6,
            padding: '10px 14px', fontSize: 11.5, color: 'var(--gray-700)', marginBottom: 16, lineHeight: 1.5,
          }}>
            Syncs orders, customers, addresses, order balances, and feedback from the production API into Neon.
            Runs automatically every 15 minutes while an Admin is logged in, plus a full nightly sync at 2 AM ET.
            Uses a service account — no IP whitelisting needed.
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--gray-700)' }}>Lookback:</label>
            <select
              value={days}
              onChange={e => setDays(Number(e.target.value))}
              style={{
                padding: '7px 12px', borderRadius: 6, border: '1px solid var(--gray-300)',
                fontSize: 12.5, fontFamily: 'inherit', cursor: 'pointer',
              }}
            >
              <option value={1}>1 day</option>
              <option value={2}>2 days</option>
              <option value={3}>3 days</option>
              <option value={7}>1 week</option>
              <option value={14}>2 weeks</option>
              <option value={30}>30 days</option>
            </select>

            <button
              onClick={runSync}
              disabled={syncing}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 18px',
                background: syncing ? 'var(--gray-400)' : 'var(--orange)',
                color: '#fff', border: 'none', borderRadius: 6,
                fontSize: 12.5, fontWeight: 700, cursor: syncing ? 'default' : 'pointer',
                fontFamily: 'inherit', transition: 'all 0.15s',
              }}
            >
              {syncing ? <Loader size={14} className="spin" /> : <RefreshCw size={14} />}
              {syncing ? 'Syncing...' : 'Run Sync'}
            </button>

            {syncResult && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                fontSize: 12, color: syncResult.ok ? 'var(--green)' : 'var(--red)',
                fontWeight: 600,
              }}>
                {syncResult.ok ? <CheckCircle size={14} /> : <XCircle size={14} />}
                {syncResult.message}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sync Log */}
      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Sync History</span>
          <button onClick={loadLogs} style={{
            background: 'none', border: '1px solid var(--gray-300)', borderRadius: 6,
            padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
            color: 'var(--gray-600)', display: 'flex', alignItems: 'center', gap: 4,
          }}>
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          {logsLoading ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)', fontSize: 12.5 }}>
              <Loader size={14} className="spin" /> Loading sync history...
            </div>
          ) : logs.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)', fontSize: 12.5 }}>
              No syncs recorded yet. Run your first sync above.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                  <th style={{ textAlign: 'center', padding: '8px 6px', fontWeight: 700, color: 'var(--gray-500)', width: 30 }}></th>
                  <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Time</th>
                  <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Days</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Orders</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Feedback</th>
                  <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Errors</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                    <td style={{ textAlign: 'center', padding: '6px' }}>{statusIcon(log.status)}</td>
                    <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                      <Clock size={11} style={{ marginRight: 4, opacity: 0.4 }} />
                      {formatDate(log.finished_at || log.started_at)}
                    </td>
                    <td style={{ textAlign: 'center', padding: '6px 8px' }}>{log.days ?? '—'}</td>
                    <td style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600 }}>
                      {log.orders_synced ?? 0}/{log.orders_total ?? 0}
                    </td>
                    <td style={{ textAlign: 'right', padding: '6px 8px' }}>{log.feedbacks_synced ?? 0}</td>
                    <td style={{ textAlign: 'right', padding: '6px 8px', color: (log.errors ?? 0) > 0 ? 'var(--red)' : 'var(--gray-500)' }}>
                      {log.errors ?? 0}
                    </td>
                    <td style={{ padding: '6px 12px', color: 'var(--gray-600)', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.message ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
